import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useEffect } from "react";
import { settingService } from "../services";
import { buildParams } from "/app/src/helpers/params";

/**
 * Initializes multiple setting operations including query management and mutations.
 *
 * @param {Object} params - The parameters for multiple settings.
 * @param {string|number} params.integrationId - The integration identifier used to fetch and update settings.
 * @param {Array} params.settingTypes - An array of setting types to be managed.
 * @returns {Object} An object containing query data and a submitHandler for updating settings.
 */
const useMultipleSettings = ({ integrationId, settingTypes }) => {
  const queryClient = useQueryClient();
  const queryKey = ["settings", integrationId, ...settingTypes];

  // Store the initial settings map to track what values were when the form loaded
  const initialSettingsMapRef = useRef(null);

  // Fetches all settings for the given integrationId and settingTypes
  const settingsQuery = useQuery({
    queryKey,
    queryFn: () =>
      settingService.getAll(
        buildParams({ integrationId, type: `[or]${settingTypes.join(";")}` }),
      ),
  });

  // Maps the settings to their respective types
  const settingsMap = settingTypes.reduce((acc, type) => {
    const setting = settingsQuery.data?.settings.find((s) => s?.type === type);
    acc[type] = setting?.value;
    return acc;
  }, {});

  // Capture initial settings map when data first loads
  useEffect(() => {
    if (settingsQuery.data && initialSettingsMapRef.current === null) {
      // Create the initial settings map from the query data
      const initialMap = settingTypes.reduce((acc, type) => {
        const setting = settingsQuery.data.settings.find(
          (s) => s?.type === type,
        );
        acc[type] = setting?.value;
        return acc;
      }, {});
      initialSettingsMapRef.current = initialMap;
    }
  }, [settingsQuery.data, settingTypes]);

  const { mutateAsync: updateSettingsMutation } = useMutation({
    mutationFn: async (formValues) => {
      const initialSettingsMap = initialSettingsMapRef.current || {};

      const promises = settingTypes
        .map((type) => {
          const formValueKey = type;
          const newValue = formValues[formValueKey]; // Get the new value from form values
          const initialValue = initialSettingsMap[formValueKey]; // Get the initial value when form loaded
          const existingSetting = settingsQuery.data?.settings.find(
            (setting) => setting?.type === type,
          );

          // If the value is the same as the existing one, skip the update
          if (existingSetting && existingSetting.value === newValue) {
            return null;
          }

          if (!existingSetting) {
            // For new settings, check if the value has meaningfully changed from the initial state
            // Initial state for non-existent settings is undefined
            // Components transform undefined to default values (e.g., false for booleans, "" for strings)
            // Only create settings if the value is different from both the stored initial value
            // AND is not an empty/default value
            const isDefaultValue =
              newValue === undefined ||
              newValue === null ||
              newValue === "" ||
              newValue === false;

            // If initial value was undefined and current value is a default, skip creation
            if (initialValue === undefined && isDefaultValue) {
              return null;
            }

            // If the value hasn't changed from the initial value, skip
            if (newValue === initialValue) {
              return null;
            }

            return settingService.createSingle({
              name: type,
              value: newValue,
              type,
              integrationId,
            });
          } else {
            return settingService.updateSingle(existingSetting.id, {
              value: newValue,
            });
          }
        })
        // Filters out null values to only include promises for changed settings
        .filter((promise) => promise !== null);

      // Execute all API calls
      return Promise.all(promises).then((results) =>
        // Merge updated settings with unchanged ones
        settingTypes.map((type) => {
          const updatedSetting = results.find(
            (result) => result?.setting?.type === type,
          );
          if (updatedSetting) {
            return updatedSetting.setting;
          }
          return settingsQuery.data?.settings.find(
            (setting) => setting?.type === type,
          );
        }),
      );
    },
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, {
        settings: response,
      });
      // Update the initial settings map ref after successful mutation
      const newSettingsMap = settingTypes.reduce((acc, type) => {
        const setting = response.find((s) => s?.type === type);
        acc[type] = setting?.value;
        return acc;
      }, {});
      initialSettingsMapRef.current = newSettingsMap;
    },
  });

  /**
   * Handles the form submission to update settings.
   *
   * @param {Object} values - The form values containing new settings.
   * @returns {Promise<void>} A promise that resolves once the settings update is processed.
   */
  const updateSettings = async (values) => {
    await updateSettingsMutation(values);
  };

  return {
    ...settingsQuery,
    ...settingsMap,
    submitHandler: updateSettings,
  };
};

export default useMultipleSettings;
